import { useEffect, useState } from 'react';
import { Box, Stack, Typography } from '@mui/material';
import ShadowBlock from '../../../atoms/ShadowBlock';
import MButton from '../../../MUI/Button/MButton';
import useSafeLocationState from '../../../../hooks/useSafeLocationState';
import { apiClient } from '../../../../lib/apiClient';
import Table from '../../../atoms/Table/Table';
import { tableColumns } from './AutomatedInvoicing.constants';
import LoaderCover from '../../../atoms/LoaderCover';
import ConfirmationModal from './ConfirmationModal';
import notificationUtils from '../../../../utils/notificationUtils';

const getNotificationStatus = ({
  isFebruaryException,
  isFromNewMonth,
  isInitialFebruaryException,
}) => {
  if (isInitialFebruaryException && isFromNewMonth) return 'february-and-next-month-exception';

  if (!isFebruaryException && isFromNewMonth) return 'next-month-exception';

  if (isFebruaryException) return 'february-exception';

  return null;
};

const AutomatedInvoicing = () => {
  useSafeLocationState('Accounting', 'Automated Invoicing Settings');
  const [automatedImportationSettings, setAutomatedImportationSettings] = useState({});
  const [updatedSettings, setUpdatedSettings] = useState({});
  const [isConfirmedModalOpened, setIsConfirmedModalOpened] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const closeConfirmedModal = () => {
    setIsConfirmedModalOpened(false);
  };

  const openConfirmedModal = () => {
    setIsConfirmedModalOpened(true);
  };

  const getAutomatedInvoicingSettings = async () => {
    setIsLoading(true);
    const { data } = await apiClient.get(`/api/automatedImportationSettings`);

    const normalizedResponse = {};
    if (data?.result && data?.result?.length) {
      data.result.forEach((item) => {
        normalizedResponse[item.id] = {
          ...item,
          initialValue: item.value,
          isInitialFebruaryException: item.value > 28,
        };
      });
    }
    setAutomatedImportationSettings(normalizedResponse);

    setIsLoading(false);
  };

  const updateSettings = async () => {
    try {
      setIsLoading(true);
      await apiClient.put(`/api/automatedImportationSettings`, {
        updatedSettings: Object.values(updatedSettings),
      });
      setUpdatedSettings({});
    } catch (error) {
      notificationUtils.error(error.message || 'Something went wrong');
    } finally {
      await getAutomatedInvoicingSettings();
      setIsLoading(false);
      closeConfirmedModal();
    }
  };

  useEffect(() => {
    getAutomatedInvoicingSettings();
  }, []);

  const updateValue = (id) => (changedValue) => {
    setAutomatedImportationSettings((settings) => ({
      ...settings,
      [id]: {
        ...settings[id],
        value: changedValue.value,
        isFebruaryException: changedValue.value > 28,
        initialValue: settings[id].initialValue ? settings[id].initialValue : settings[id].value,
      },
    }));
    setUpdatedSettings((settings) => {
      if (automatedImportationSettings[id].initialValue === changedValue.value) {
        const copiedSettings = { ...settings };
        delete copiedSettings[id];
        return copiedSettings;
      }
      return { ...settings, [id]: { id, value: changedValue.value } };
    });
  };

  const getRows = (data) => {
    const result = {};

    Object.keys(data).forEach((itemKey) => {
      const {
        name,
        state,
        invoiceType,
        value,
        isFromNewMonth,
        id,
        isFebruaryException,
        isInitialFebruaryException,
      } = data[itemKey];

      const key = `${invoiceType}-${state}`;
      const notificationType = getNotificationStatus({
        isFebruaryException,
        isFromNewMonth,
        isInitialFebruaryException,
      });

      if (!result[name]) {
        result[name] = { name };
      }

      result[name][key] = value;

      result[name] = {
        ...result[name],
        [key]: {
          value,
          notificationType,
          changeDate: updateValue(id),
        },
      };
    });

    return Object.values(result);
  };

  const isDisabledSaveButton = !Object.keys(updatedSettings).length;

  return (
    <Stack>
      {isLoading && <LoaderCover isFixed />}
      <Typography variant="h4" margin="32px 32px 64px" data-testid="page_title">
        Automated Invoicing Settings
      </Typography>
      <Box width="1264px" margin="0 auto">
        <ShadowBlock>
          <Stack position="relative">
            <Box
              sx={{
                width: '100%',
                height: '40px',
                display: 'grid',
                gridTemplateColumns: '200px 1fr 1fr 1fr',
                alignItems: 'center',
                backgroundColor: '#ebedfa',
                borderBottom: '1px solid #DCE4FF',
              }}
            >
              <Box />
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="bodyM" sx={{ color: '#54577a', fontWeight: 600 }}>
                  Arizona
                </Typography>
              </Box>
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="bodyM" sx={{ color: '#54577a', fontWeight: 600 }}>
                  Nevada
                </Typography>
              </Box>
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="bodyM" sx={{ color: '#54577a', fontWeight: 600 }}>
                  New Mexico
                </Typography>
              </Box>
            </Box>
            {automatedImportationSettings ? (
              <Table
                columns={tableColumns}
                rows={getRows(automatedImportationSettings)}
                overflowHidden={false}
              />
            ) : null}
          </Stack>
          <Stack direction="row" justifyContent="end" gap={3}>
            <MButton
              data-testid="billingInstancesButton"
              type="button"
              size="large"
              disabled={isDisabledSaveButton}
              onClick={openConfirmedModal}
            >
              Save
            </MButton>
          </Stack>
        </ShadowBlock>
      </Box>
      <ConfirmationModal
        closeConfirmedModal={closeConfirmedModal}
        isConfirmedModalOpened={isConfirmedModalOpened}
        updateSettings={updateSettings}
      />
    </Stack>
  );
};

export default AutomatedInvoicing;
